import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import { Button, Modal, ModalBody, ModalFooter, Paragraph, Text } from '@customerio/pluma-components/react';
import { useServicesMutation } from '@/services/api';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbars } from '@customerio/pluma-components/react';
import { sentryCaptureException } from '@/utils/sentry';
import type { IdentifierType } from '../types';

export interface UnsuppressConfirmModalData {
  identifiers: string[];
  workspaceName: string;
  workspaceId: number;
  identifierType: IdentifierType;
}

export function UnsuppressConfirmModal({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<UnsuppressConfirmModalData>) {
  const snackbars = useSnackbars();
  const queryClient = useQueryClient();
  const { identifiers, workspaceName, workspaceId, identifierType } = data;

  const unsuppressMutation = useServicesMutation('unsuppressCustomers', {
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ['getSuppressionCount'] });
      snackbars.create({
        variant: response.matched.length > 0 ? 'success' : 'default',
        id: 'workspace-suppressions.unsuppress-result',
        message: response.message,
      });
      onConfirm();
    },
    onError: (error) => {
      sentryCaptureException(error);
      snackbars.create({
        variant: 'error',
        id: 'workspace-suppressions.unsuppress-error',
        message: 'Could not unsuppress. Check the identifiers and try again.',
      });
    },
  });

  const handleUnsuppress = () => {
    const body = identifierType === 'email' ? { emails: identifiers } : { customer_ids: identifiers };
    unsuppressMutation.mutate({
      params: { workspaceId },
      body,
    });
  };

  const isPending = unsuppressMutation.isPending;

  const displayText =
    identifiers.length === 1
      ? identifiers[0]
      : `${identifiers.length} identifiers (${identifiers.slice(0, 3).join(', ')}${identifiers.length > 3 ? '…' : ''})`;

  return (
    <Modal
      state={modalState}
      onClose={onClose}
      title={identifiers.length === 1 ? 'Unsuppress this identifier?' : 'Unsuppress these identifiers?'}
      size="md"
      shouldShowCloseButton={!isPending}
      shouldCloseOnOverlayClick={!isPending}
    >
      <ModalBody>
        <Paragraph>
          <Text weight="bold" as="span">
            {displayText}
          </Text>{' '}
          will be unsuppressed in {workspaceName}. You can add {identifiers.length === 1 ? 'a person' : 'people'} with{' '}
          {identifiers.length === 1 ? 'this identifier' : 'these identifiers'} again; they will have no prior message or
          journey history.
        </Paragraph>
      </ModalBody>
      <ModalFooter>
        <Button variant="subtle" onClick={onClose} isDisabled={isPending}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleUnsuppress} isLoading={isPending}>
          Unsuppress
        </Button>
      </ModalFooter>
    </Modal>
  );
}
